import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldIcon from "@mui/icons-material/ShieldOutlined";
import PeopleIcon from "@mui/icons-material/PersonOutlineOutlined";
import { toast } from "react-toastify";
import { RoleApi } from "../../../apis";
import { PageHeader, EmptyState, FormSheet, ConfirmDialog } from "../../../components/ui";
import { layout } from "../../../theme/tokens";

const PORTAL_LABELS = {
  admin: "Admin area",
  user: "My work area",
};

const PORTAL_HELP = {
  admin:
    "Members land in the admin area: the management screens, filtered to whatever this role may see.",
  user: "Members land in their own workspace: their timesheet, their expenses, their dashboard.",
};

const emptyDraft = { name: "", description: "", portal: "user" };

/**
 * Roles & Permissions.
 *
 * Pick a role on the left, tick what it may do on the right, save. Each tick
 * is one permission key; the same keys guard the API, so what is shown here
 * is the actual boundary rather than a description of one.
 *
 * Two roles behave differently on purpose:
 *   - Admin is read-only. Everything an administrator can do flows from
 *     roles.manage, so a slip that revoked it here would leave nobody able to
 *     put it back.
 *   - The other built-in roles can be re-permissioned but not renamed or
 *     deleted, so there is always a known way in.
 */
export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  // The ticked set being edited, kept separate from the saved role so the
  // grid can be changed and abandoned.
  const [draftPermissions, setDraftPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [creating, setCreating] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [roleRes, catalogRes] = await Promise.all([
        RoleApi.getAllRoles(),
        RoleApi.getPermissionCatalog(),
      ]);

      const roleList = roleRes?.data?.data?.roles || [];
      setRoles(roleList);
      setModules(catalogRes?.data?.data?.modules || []);

      // Keep the current selection across a reload where possible.
      setSelectedId((current) =>
        current && roleList.some((r) => r.id === current) ? current : roleList[0]?.id ?? null
      );
    } catch (error) {
      // The shared axios interceptor already toasts the server's message on
      // a failure, so this only needs to stop the screen half-rendering.
      console.error("Failed to load roles:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => roles.find((role) => role.id === selectedId) || null,
    [roles, selectedId]
  );

  // Reset the grid whenever the selected role changes or is reloaded.
  useEffect(() => {
    setDraftPermissions(selected ? [...selected.permissions] : []);
  }, [selected]);

  const isAdminRole = Boolean(selected?.isSystem && selected?.name === "Admin");
  const readOnly = isAdminRole;

  const held = useMemo(() => new Set(draftPermissions), [draftPermissions]);

  const dirty = useMemo(() => {
    if (!selected || readOnly) return false;
    const saved = new Set(selected.permissions);
    if (saved.size !== held.size) return true;
    return [...held].some((key) => !saved.has(key));
  }, [selected, held, readOnly]);

  const togglePermission = (key) => {
    setDraftPermissions((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleModule = (moduleKeys, allOn) => {
    setDraftPermissions((prev) => {
      const without = prev.filter((key) => !moduleKeys.includes(key));
      return allOn ? without : [...without, ...moduleKeys];
    });
  };

  const savePermissions = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const res = await RoleApi.setRolePermissions(selected.id, draftPermissions);
      const updated = res?.data?.data?.role;
      if (updated) {
        setRoles((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      }
      // No toast here: the interceptor raises one for every successful write.
    } catch (error) {
      console.error("Failed to save permissions:", error);
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    const name = draft.name.trim();
    if (!name) {
      toast.error("Give the role a name");
      return;
    }
    try {
      setCreating(true);
      // Created with no permissions: the grid is where they get granted, so
      // a new role starts unable to do anything rather than guessing.
      const res = await RoleApi.createRole({
        name,
        description: draft.description.trim() || undefined,
        portal: draft.portal,
        permissions: [],
      });
      const created = res?.data?.data?.role;
      setCreateOpen(false);
      setDraft(emptyDraft);
      if (created) {
        setRoles((prev) => [...prev, created]);
        setSelectedId(created.id);
      } else {
        await load();
      }
      // A create answers 201, which the interceptor does not toast — and
      // this one says what to do next, which its message does not.
      toast.success(`${name} created — now choose what it can do`);
    } catch (error) {
      console.error("Failed to create role:", error);
    } finally {
      setCreating(false);
    }
  };

  const deleteRole = async () => {
    if (!confirmDelete) return;
    try {
      setDeleting(true);
      await RoleApi.deleteRole(confirmDelete.id);
      setRoles((prev) => prev.filter((r) => r.id !== confirmDelete.id));
      setSelectedId((current) =>
        current === confirmDelete.id ? roles.find((r) => r.id !== confirmDelete.id)?.id ?? null : current
      );
      setConfirmDelete(null);
    } catch (error) {
      console.error("Failed to delete role:", error);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Roles & Permissions" />
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Roles & Permissions"
        subtitle={`${roles.length} role${roles.length === 1 ? "" : "s"}`}
        actions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => setCreateOpen(true)}
          >
            New role
          </Button>
        }
      />

      <Grid container spacing={2}>
        {/* ── Roles ─────────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Stack spacing={1.25}>
            {roles.map((role) => {
              const active = role.id === selectedId;
              return (
                <Card
                  key={role.id}
                  onClick={() => setSelectedId(role.id)}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    borderLeft: 3,
                    borderLeftColor: active ? "primary.main" : "transparent",
                    bgcolor: active ? "action.selected" : undefined,
                    transition: "background-color .15s ease",
                    "&:hover": { bgcolor: active ? "action.selected" : "action.hover" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                          {role.name}
                        </Typography>
                        {role.isSystem && (
                          <Tooltip title="Built in: can be re-permissioned, but not renamed or deleted">
                            <LockOutlinedIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                          </Tooltip>
                        )}
                      </Box>
                      {role.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.25, display: "block" }}
                        >
                          {role.description}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={PORTAL_LABELS[role.portal] || role.portal}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<PeopleIcon sx={{ fontSize: 14 }} />}
                          label={role.userCount}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<ShieldIcon sx={{ fontSize: 14 }} />}
                          label={`${role.permissions.length} allowed`}
                        />
                      </Stack>
                    </Box>
                    {!role.isSystem && (
                      <IconButton
                        size="small"
                        aria-label={`Delete ${role.name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setConfirmDelete(role);
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18, color: "error.main" }} />
                      </IconButton>
                    )}
                  </Box>
                </Card>
              );
            })}
          </Stack>
        </Grid>

        {/* ── What the selected role may do ─────────────────────── */}
        <Grid item xs={12} md={8}>
          {!selected ? (
            <EmptyState
              icon={ShieldIcon}
              title="No roles yet"
              description="Create a role to decide what its members can do."
            />
          ) : (
            // overflow must stay visible: MUI Card clips by default, and a
            // clipped ancestor silently turns the sticky header below into an
            // ordinary one that scrolls away.
            <Card sx={{ p: { xs: 2, sm: 2.5 }, pt: 0, overflow: "visible" }}>
              {/* Sticky, and below the fixed top bar: the grid runs to ten
                  modules, and a Save button at the far end of that is a
                  button you have to go looking for. */}
              <Box
                sx={{
                  position: "sticky",
                  top: {
                    xs: `calc(${layout.topBarHeight}px + env(safe-area-inset-top))`,
                    md: `${layout.topBarHeight}px`,
                  },
                  zIndex: 2,
                  bgcolor: "background.paper",
                  pt: { xs: 2, sm: 2.5 },
                  pb: 1.5,
                  mb: 0.5,
                  borderBottom: 1,
                  borderColor: "border.subtle",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    What {selected.name} can do
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {readOnly
                      ? "The Admin role always holds every permission. Create a custom role to grant a narrower set."
                      : PORTAL_HELP[selected.portal]}
                  </Typography>
                </Box>

                {!readOnly && (
                  <Box sx={{ display: "flex", gap: 1, flexShrink: 0, ml: "auto" }}>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setDraftPermissions([...selected.permissions])}
                      disabled={!dirty || saving}
                    >
                      Discard
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={savePermissions}
                      disabled={!dirty || saving}
                    >
                      {saving ? "Saving…" : "Save changes"}
                    </Button>
                  </Box>
                )}
              </Box>

              <Stack divider={<Divider />} spacing={0}>
                {modules.map((module) => {
                  const moduleKeys = module.permissions.map((p) => p.key);
                  const onCount = moduleKeys.filter((key) => held.has(key)).length;
                  const allOn = onCount === moduleKeys.length;

                  return (
                    <Box key={module.key} sx={{ py: 1.75 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {module.label}
                        </Typography>
                        {!readOnly && (
                          <Button
                            size="small"
                            onClick={() => toggleModule(moduleKeys, allOn)}
                            sx={{ minWidth: 0, flexShrink: 0 }}
                          >
                            {allOn ? "Clear all" : "Select all"}
                          </Button>
                        )}
                      </Box>

                      <Grid container spacing={0.5}>
                        {module.permissions.map((permission) => (
                          <Grid item xs={12} sm={6} key={permission.key}>
                            <FormControlLabel
                              sx={{ alignItems: "flex-start", mr: 0, ml: -1 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={held.has(permission.key)}
                                  disabled={readOnly}
                                  onChange={() => togglePermission(permission.key)}
                                  sx={{ pt: 0.75 }}
                                />
                              }
                              label={
                                <Box sx={{ py: 0.5 }}>
                                  <Typography variant="body2">{permission.label}</Typography>
                                  {permission.description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", lineHeight: 1.4 }}
                                    >
                                      {permission.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  );
                })}
              </Stack>

            </Card>
          )}
        </Grid>
      </Grid>

      {/* ── New role ────────────────────────────────────────────── */}
      <FormSheet
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setDraft(emptyDraft);
        }}
        title="New role"
        subtitle="Name it and pick its workspace. You'll choose what it can do next."
        submitLabel="Create role"
        onSubmit={createRole}
        submitting={creating}
        submitDisabled={!draft.name.trim()}
      >
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Role name"
            value={draft.name}
            onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
            placeholder="Accountant"
            fullWidth
            autoFocus
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, description: event.target.value }))
            }
            placeholder="What this role is for — shown on this screen only."
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 255 }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Workspace
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Which part of the app members sign in to. Permissions then decide what they see
              inside it.
            </Typography>
            <RadioGroup
              value={draft.portal}
              onChange={(event) => setDraft((prev) => ({ ...prev, portal: event.target.value }))}
            >
              {Object.keys(PORTAL_LABELS).map((portal) => (
                <FormControlLabel
                  key={portal}
                  value={portal}
                  control={<Radio size="small" />}
                  sx={{ alignItems: "flex-start", mb: 0.5 }}
                  label={
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body2">{PORTAL_LABELS[portal]}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", lineHeight: 1.4 }}
                      >
                        {PORTAL_HELP[portal]}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
        </Stack>
      </FormSheet>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={deleteRole}
        busy={deleting}
        title={confirmDelete ? `Delete ${confirmDelete.name}?` : "Delete role?"}
        description={
          confirmDelete?.userCount
            ? `${confirmDelete.userCount} user${
                confirmDelete.userCount === 1 ? " holds" : "s hold"
              } this role. Move them to another role first — this will not go through until you do.`
            : "The role and its permissions are removed. This cannot be undone."
        }
      />
    </Box>
  );
}
